import { Button, DatePicker, Form, Grid, Input, Select } from "antd";
import { ContentFilters } from "components";
import { StandardItemsWrapper } from "components/content-filters/content-filters.styles";
import dayjs from "dayjs";
import {
  UsersWithWithdrawFilterFormField,
  UsersWithWithdrawFilterFormValues,
} from "features/withdraw/components/users-with-wallet-filter-form/type";
import { useGetAmountFilterConfig } from "features/withdraw/use-cases/create-amount-filter";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { DATE_FORMATS } from "utils/date";

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

type UsersWithWalletFilterFormProps = {
  formId: string;
  handleFilter: (values: UsersWithWithdrawFilterFormValues) => void;
  initialValues: UsersWithWithdrawFilterFormValues;
  setQueryFilters: Dispatch<SetStateAction<any>>;
  closeModal?: () => void;
};

export const UsersWithWithdrawFilterForm = ({
  formId,
  handleFilter,
  initialValues,
  setQueryFilters,
  closeModal,
}: UsersWithWalletFilterFormProps) => {
  const [validateTrigger, setValidateTrigger] = useState(["onSubmit"]);
  const [form] = Form.useForm<UsersWithWithdrawFilterFormValues>();
  const { t } = useTranslation();
  const screens = useBreakpoint();

  const { data: amountFilterConfig, isLoading: isAmountFilterConfigLoading } =
    useGetAmountFilterConfig();

  const onReset = () => {
    form.resetFields();
    handleFilter({ ...initialValues });
    setQueryFilters({ ...initialValues });
  };

  const handleOnFinish = (values: UsersWithWithdrawFilterFormValues) => {
    handleFilter(values);
    setQueryFilters(values);
    closeModal?.();
  };

  const amountRangeOptions = (() => {
    if (!amountFilterConfig) {
      return [];
    }

    const minAmount = Number(amountFilterConfig.min_amount);
    const maxAmount = Number(amountFilterConfig.max_amount);
    const rangeInterval = Number(amountFilterConfig.range_interval);

    if (
      !Number.isFinite(minAmount) ||
      !Number.isFinite(maxAmount) ||
      !Number.isFinite(rangeInterval) ||
      rangeInterval <= 0 ||
      minAmount >= maxAmount
    ) {
      return [];
    }

    const options: {
      value: string;
      label: string;
    }[] = [];

    for (let start = minAmount; start < maxAmount; start += rangeInterval) {
      const end = Math.min(start + rangeInterval, maxAmount);

      options.push({
        value: `${start}-${end}`,
        label: `${start} KD - ${end} KD`,
      });
    }

    options.push({
      value: `${maxAmount}+`,
      label: `${maxAmount}+ KD`,
    });

    return options;
  })();

  return (
    <div className="form_main_wrap">
      <Form<UsersWithWithdrawFilterFormValues>
        id={formId}
        name={formId}
        form={form}
        layout="vertical"
        onFinish={handleOnFinish}
        initialValues={initialValues}
        onFinishFailed={() => {
          setValidateTrigger(["onChange"]);
        }}
        validateTrigger={validateTrigger}
      >
        <div className="withdraw_form_wrap">
          <ContentFilters.FormItemsWrapper className="withdraw_form">
            <StandardItemsWrapper className="withdraw_form_list">
              <Form.Item<UsersWithWithdrawFilterFormValues>
                label={t("withdraw.formfield.username.label")}
                name={UsersWithWithdrawFilterFormField.FullUserName}
              >
                <Input
                  placeholder={t("withdraw.formfield.username.placeholder")}
                />
              </Form.Item>
              <Form.Item<UsersWithWithdrawFilterFormValues>
                label={t("withdraw.formfield.phonenumber.label")}
                name={UsersWithWithdrawFilterFormField.PhoneNumber}
                style={{ width: "230px" }}
              >
                <Input
                  placeholder={t("withdraw.formfield.phonenumber.placeholder")}
                />
              </Form.Item>
              {/* <Form.Item<UsersWithWithdrawFilterFormValues>
              label={t('withdraw.formfield.referencenumber.label')}
              name={UsersWithWithdrawFilterFormField.ReferenceNumber}
            >
              <Input
                placeholder={t(
                  'withdraw.formfield.referencenumber.placeholder',
                )}
              />
            </Form.Item> */}

              <Form.Item<UsersWithWithdrawFilterFormValues>
                label={t("withdraw.formfield.status.label")}
                name={UsersWithWithdrawFilterFormField.status}
                className="cstm_width"
                style={{ width: "240px" }}
              >
                <Select
                  placeholder="All"
                  allowClear
                  options={[
                    { value: "1", label: "Pending" },
                    { value: "2", label: "Approved" },
                    { value: "3", label: "Rejected" },
                    { value: "4", label: "Approval Processing" },
                    { value: "5", label: "Rejection Processing" },
                  ]}
                />
              </Form.Item>
              <Form.Item<UsersWithWithdrawFilterFormValues>
                label={"Auth Id"}
                name={UsersWithWithdrawFilterFormField.Auth}
              >
                <Input placeholder={"Enter Auth Id"} />
              </Form.Item>
              <Form.Item<UsersWithWithdrawFilterFormValues>
                label={t("withdraw.formfield.transactionAmount.label")}
                name={UsersWithWithdrawFilterFormField.TransactionAmount}
              >
                <Select
                  style={{ width: "150px" }}
                  placeholder={t(
                    "withdraw.formfield.transactionAmount.placeholder"
                  )}
                  loading={isAmountFilterConfigLoading}
                  options={amountRangeOptions}
                  allowClear
                />
              </Form.Item>
              <Form.Item<UsersWithWithdrawFilterFormValues>
                label={"Order Id"}
                name={UsersWithWithdrawFilterFormField.Tranasction}
              >
                <Input placeholder={"Enter Order Id"} />
              </Form.Item>
              <Form.Item<UsersWithWithdrawFilterFormValues>
                label={t("withdraw.formfield.transactionDate.label")}
                name={UsersWithWithdrawFilterFormField.TransactionDate}
              >
                <RangePicker
                  format={DATE_FORMATS.date}
                  presets={[
                    {
                      label: t("form.formFields.date.presets.today"),
                      value: [dayjs(), dayjs()],
                    },
                    {
                      label: t("form.formFields.date.presets.fromTomorrow"),
                      value: [dayjs().add(1, "day"), null],
                    },
                    {
                      label: t("form.formFields.date.presets.untilYesterday"),
                      value: [null, dayjs().add(-1, "day")],
                    },
                  ]}
                />
              </Form.Item>
              <Form.Item<UsersWithWithdrawFilterFormValues>
                label={t("withdraw.formfield.date.label")}
                name={UsersWithWithdrawFilterFormField.date}
              >
                <RangePicker
                  format={DATE_FORMATS.date}
                  presets={[
                    {
                      label: t("form.formFields.date.presets.today"),
                      value: [dayjs(), dayjs()],
                    },
                    {
                      label: t("form.formFields.date.presets.fromTomorrow"),
                      value: [dayjs().add(1, "day"), null],
                    },
                    {
                      label: t("form.formFields.date.presets.untilYesterday"),
                      value: [null, dayjs().add(-1, "day")],
                    },
                  ]}
                />
              </Form.Item>
            </StandardItemsWrapper>
          </ContentFilters.FormItemsWrapper>
        </div>
        {screens.lg && (
          <ContentFilters.ButtonsWrapper className="withdraw_btn">
            <Button type="primary" htmlType="submit">
              {t("wallet.buttons.filter", { count: 1 })}
            </Button>
            <Button onClick={onReset}>{t("wallet.buttons.reset")}</Button>
          </ContentFilters.ButtonsWrapper>
        )}
      </Form>
    </div>
  );
};
